<!-- eslint-disable vue/no-v-html -->
<template>
  <form @submit.prevent="onSubmit">
    <div class="mb-6 grid w-full gap-6 md:grid-cols-6">
      <VehicleStatus
        :milage="formValues.milage"
        :fuel-level="formValues.fuelLevel"
        :show-tyre-condition-fields="true"
        :tyre-condition-front="formValues.tyreConditionFront"
        :tyre-condition-rear="formValues.tyreConditionRear"
        :needs-tyre-check="car.needsTyreCheck"
        class="md:col-span-4"
        :fuel-type="fueltype"
        :show-milage-not-updated="!car.readyToHandover && !milageChanged"
        :show-fuel-level-not-updated="!car.readyToHandover && !fuelLevelChanged"
        :show-milage-warning="
          !!formValues.milage && milageChanged && formValues.milage < car.milage
        "
        @update:milage="setFieldValue('milage', $event)"
        @update:fuel-level="setFieldValue('fuelLevel', $event)"
        @update:tyre-condition-front="setFieldValue('tyreConditionFront', $event)"
        @update:tyre-condition-rear="setFieldValue('tyreConditionRear', $event)"
        @fuel-level-touched="fuelLevelChanged = true"
      />
      <div class="space-y-2 md:col-span-2">
        <LabeledText v-if="car.milage" :label="t('carMilage')">
          <p>
            {{ `${car.milage} km` }}
          </p>
        </LabeledText>
        <LabeledText v-if="lastBooking" :label="t('lastBooking')">
          <p class="flex items-center justify-between">
            <CopyToClipboardButton :text="lastBooking.bookingNumber" class="my-2">
              {{ lastBooking.bookingNumber }}
            </CopyToClipboardButton>
            <router-link :to="{ name: 'bookingEdit', params: { id: lastBooking.id } }">{{
              t('showBooking')
            }}</router-link>
          </p>
        </LabeledText>
      </div>
    </div>
    <div class="space-y-4">
      <Checkbox v-model="carWashedChecked" :label="t('carWashed')" />
      <Checkbox v-model="carVacuumedChecked" :label="t('carVacuumed')" />
      <Checkbox
        v-model="fuelLevelAndMilageUpdatedChecked"
        :label="t('fuelLevelAndMilageUpdated')"
      />
    </div>
    <div class="mt-5 grid gap-2 md:flex md:justify-end">
      <CVButton
        :is-loading="isSaving"
        type="submit"
        :variant="car.readyToHandover ? 'default' : 'secondary'"
      >
        {{ t('save') }}
      </CVButton>
      <CVButton
        v-if="!car.readyToHandover"
        :disabled="!readyToHandover"
        :is-loading="isSaving"
        type="submit"
        variant="default"
        name="readyToHandover"
      >
        {{ t('saveAndReadyToHandover') }}
      </CVButton>
    </div>
  </form>

  <Divider class="col-span-3" />

  <Damages :car-id="car.id" />
</template>

<script lang="ts" setup>
import Damages from '@/components/booking-forms/damages/Damages.vue';
import VehicleStatus from '@/components/booking-forms/VehicleStatus.vue';
import Checkbox from '@/components/Checkbox.vue';
import Divider from '@/components/Divider.vue';
import type { Car, CarStatusFormValues } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { computed, reactive, ref } from 'vue';
import LabeledText from '@/components/LabeledText.vue';
import { TyreCondition } from '@carvia/ros-client-types';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';

const props = defineProps<{
  car: Car;
  isSaving?: boolean;
  autoEnterMilage?: boolean;
  lastBooking?: { id: string; bookingNumber: number };
}>();
const emit = defineEmits<{
  (e: 'onSubmit', value: CarStatusFormValues): void;
}>();

const { t } = useI18n();

const initialValues = reactive({
  milage: props.autoEnterMilage ? props.car.milage : null,
  fuelLevel: props.car.fuelLevel,
  tyreConditionFront: props.car.needsTyreCheck ? undefined : props.car.tyreConditionFront,
  tyreConditionRear: props.car.needsTyreCheck ? undefined : props.car.tyreConditionRear,
});

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm<{
  milage: number | null;
  fuelLevel: number;
  tyreConditionFront?: TyreCondition;
  tyreConditionRear?: TyreCondition;
}>({
  initialValues,
});

const onSubmit = handleSubmit(async (values) => {
  const milage = values.milage ?? props.car.milage;
  const isReadyToHandover =
    ((window.event as SubmitEvent)?.submitter as HTMLButtonElement)?.name === 'readyToHandover' ||
    undefined;
  emit('onSubmit', {
    ...values,
    milage,
    readyToHandover: isReadyToHandover,
  });
});

const fueltype = computed(() => props.car.fuelType ?? props.car.vehicleType.fuelType);
const fuelLevelChanged = ref(false);
const milageChanged = computed(() => {
  return formValues.milage !== null;
});
const carWashedChecked = ref(props.car.readyToHandover);
const carVacuumedChecked = ref(props.car.readyToHandover);
const fuelLevelAndMilageUpdatedChecked = ref(props.car.readyToHandover);

const readyToHandover = computed(
  () =>
    carWashedChecked.value &&
    carVacuumedChecked.value &&
    fuelLevelAndMilageUpdatedChecked.value &&
    ((formValues.tyreConditionFront && formValues.tyreConditionRear) || !props.car.needsTyreCheck),
);
</script>

<i18n lang="json">
{
  "en": {
    "saveStatus": "Save Status",
    "readyToHandover": "<span class='font-medium'>Milage</span> and <span class='font-medium'>fuel</span> are up to date and the car is <span class='font-medium'>vacuumed</span> and <span class='font-medium'>washed</span>",
    "carMilage": "Car Milage",
    "carWashed": "The Car is washed",
    "carVacuumed": "The Car Interior is vacuumed and cleaned",
    "fuelLevelAndMilageUpdated": "Milage and Fuellevel are updated",
    "showBooking": "Show Booking",
    "lastBooking": "Last Booking",
    "saveAndReadyToHandover": "Save & Ready to Handover"
  },
  "de": {
    "saveStatus": "Status aktualisieren",
    "readyToHandover": "<span class='font-medium'>Kilometerstand</span> und <span class='font-medium'>Tank</span> sind aktuell und das Auto ist <span class='font-medium'>gesaugt</span> und <span class='font-medium'>gewaschen</span>",
    "carMilage": "Bisheriger Kilometerstand",
    "carWashed": "Das Auto wurde gewaschen",
    "carVacuumed": "Das Interieur wurde gesaugt und gereinigt",
    "fuelLevelAndMilageUpdated": "Kilometer- und Füllstand wurden aktualisiert",
    "showBooking": "Buchung anzeigen",
    "lastBooking": "Letzte Buchung",
    "saveAndReadyToHandover": "Speichern & Bereit zur Übergabe ✨"
  },
  "es": {
    "saveStatus": "Guardar estado",
    "readyToHandover": "El <span class='font-medium'>kilometraje</span> y el <span class='font-medium'>combustible/carga</span> están al día y el coche está <span class='font-medium'>aspirado</span> y <span class='font-medium'>lavado</span>",
    "carMilage": "Kilometraje del vehículo",
    "carWashed": "El coche está lavado",
    "carVacuumed": "El interior está aspirado y limpio",
    "fuelLevelAndMilageUpdated": "Kilometraje y nivel de combustible/carga actualizados",
    "showBooking": "Ver reserva",
    "lastBooking": "Última reserva",
    "saveAndReadyToHandover": "Guardar y listo para entrega ✨"
  }
}
</i18n>
